/**
 * Files, on their way up and once they are there.
 *
 * Three places attach files to a help desk record — the ticket form, the
 * message composer, the note sheet — and legacy gave each its own uploader
 * built on a shared modal with a crop step, a description field and a fifty
 * megabyte ceiling that the ticket form then overrode to none at all. One
 * control here: a drop zone, a row of chips, and the server's own two rules
 * said out loud, so a file it would refuse is turned away before it is read.
 */

import { ChevronLeft, ChevronRight, FileText, Paperclip, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { FilePreview, isImageData } from "@/components/file-preview";
import { Button, IconButton } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  type Attachment,
  type AttachmentDraft,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_MAX_MB,
} from "@/services/helpdesk";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

const chipClass =
  "bg-surface-inset text-fg-muted text-footnote hover:bg-surface-sunken flex items-center " +
  "gap-1.5 rounded-full px-3 py-1 transition-colors";

/**
 * One file, shown as what it is.
 *
 * A picture is shown as the picture. A name in a pill tells you a screenshot
 * was attached; the screenshot tells you what it is of, which on a help desk
 * is the whole of the message most of the time — and it is what the person
 * attaching it thinks they have sent.
 *
 * Anything else stays a pill, and so does a picture the browser turns out not
 * to be able to draw: `onError` falls back rather than leaving a broken frame,
 * which is what a phone's HEIC looks like in most browsers.
 */
function FileChip({
  file,
  onRemove,
  onOpen,
}: {
  file: AttachmentDraft;
  onRemove?: (() => void) | undefined;
  /** Where a picture opens. Without one it opens the viewer on its own. */
  onOpen?: (() => void) | undefined;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  // A file only chosen is shown from an object URL, which is `blob:` and says
  // nothing about what it holds — so the file itself is asked. A stored one is
  // read from its URL, as everything else in the app reads one.
  const looksLikePicture = file.file
    ? file.file.type.startsWith("image/")
    : isImageData(file.url);
  const isPicture = looksLikePicture && !broken;

  const remove = onRemove ? (
    <IconButton
      label={t("hd.removeFile").replace("{name}", file.name)}
      size="icon-sm"
      onClick={onRemove}
    >
      <X aria-hidden />
    </IconButton>
  ) : null;

  if (isPicture) {
    return (
      <>
        <span className="relative inline-flex">
          <button
            type="button"
            onClick={() => (onOpen ? onOpen() : setOpen(true))}
            title={file.name}
            className="border-line rounded-card block overflow-hidden border transition-opacity hover:opacity-90"
          >
            <img
              src={file.url}
              alt={file.name}
              onError={() => setBroken(true)}
              // A row of thumbnails reads as a row when they share a height and
              // crop to it; letting each keep its own shape makes a ragged line
              // of different-sized boxes.
              className="h-28 w-auto max-w-56 object-cover"
            />
          </button>
          {remove ? (
            // On the picture rather than beside it: the thumbnail is the whole
            // control, and a button next to it would have nothing to sit on.
            <span className="bg-surface-raised absolute top-1 right-1 rounded-full shadow-sm">
              {remove}
            </span>
          ) : null}
        </span>
        {open ? (
          <FilePreview
            src={file.url}
            title={file.name}
            download={file.name}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <span className={cn(chipClass, "max-w-56", onRemove ? "py-1 pr-1 pl-3" : undefined)}>
      <a href={file.url} download={file.name} className="flex min-w-0 items-center gap-1.5">
        <FileText className="size-3.5 shrink-0" aria-hidden />
        <span className="truncate">{file.name}</span>
      </a>
      {remove}
    </span>
  );
}

/**
 * Pick files, see what is picked, take one back off.
 *
 * The list is the whole value: a form holding it sends it as `attachments`, and
 * the API replaces what the record had with it. So removing a chip and saving
 * is how a file is detached — there is no second route for that, and no
 * "marked for deletion" state to get out of step with the list. A file already
 * on the record passes through the save untouched, because it goes back by the
 * handle it was read under.
 */
export function AttachmentPicker({
  value,
  onChange,
  disabled,
  id = "attachments",
}: {
  value: AttachmentDraft[];
  onChange: (next: AttachmentDraft[]) => void;
  disabled?: boolean | undefined;
  id?: string;
}) {
  const t = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const full = value.length >= ATTACHMENT_MAX_COUNT;

  /**
   * The object URLs this picker has made, so they can be handed back.
   *
   * A thumbnail of a file that is only chosen is drawn from the file itself,
   * and the browser holds the blob alive until the URL is revoked. Kept in a
   * ref rather than derived from `value`, because a file removed from the list
   * still needs its URL letting go of.
   */
  const shown = useRef<string[]>([]);
  useEffect(
    () => () => {
      for (const url of shown.current) URL.revokeObjectURL(url);
      shown.current = [];
    },
    [],
  );

  const take = (picked: File[]) => {
    if (picked.length === 0) return;
    const room = ATTACHMENT_MAX_COUNT - value.length;
    const chosen = picked.slice(0, room);
    const tooBig = chosen.find((file) => file.size > ATTACHMENT_MAX_MB * 1024 * 1024);
    if (tooBig) {
      setError(
        t("hd.fileTooLarge")
          .replace("{name}", tooBig.name)
          .replace("{mb}", String(ATTACHMENT_MAX_MB)),
      );
      return;
    }
    setError(
      chosen.length < picked.length
        ? t("hd.tooManyFiles").replace("{n}", String(ATTACHMENT_MAX_COUNT))
        : null,
    );
    // Held, not sent. The save uploads what is still holding a `file`; until
    // then this is a choice somebody can change their mind about without
    // leaving anything behind in the bucket.
    const drafts = chosen.map((file) => {
      const url = URL.createObjectURL(file);
      shown.current.push(url);
      return { name: file.name, key: "", url, file };
    });
    onChange([...value, ...drafts]);
  };

  return (
    <div className="flex flex-col gap-2">
      {/*
        A button that opens the picker, rather than a `<label>` wrapped round
        the input. The field's own label already points at this input by id, and
        a second label wrapping it means one click activates it twice — the
        dialog opens, the duplicate click lands on it, and on some browsers
        nothing comes back at all. A button has one behaviour and it is the one
        written here.
      */}
      <button
        type="button"
        disabled={disabled || full}
        onClick={() => input.current?.click()}
        className={cn(
          "border-line-strong text-fg-muted text-subhead rounded-card flex cursor-pointer",
          "items-center justify-center gap-2 border border-dashed px-3 py-2.5 transition-colors",
          "hover:border-accent hover:bg-accent-soft/40",
          "disabled:pointer-events-none disabled:opacity-50",
        )}
      >
        <Paperclip className="size-4 shrink-0" aria-hidden />
        <span>{full ? t("hd.attachmentsFull") : t("hd.attachFiles")}</span>
      </button>
      <input
        ref={input}
        id={id}
        type="file"
        multiple
        className="sr-only"
        {...(ATTACHMENT_ACCEPT ? { accept: ATTACHMENT_ACCEPT } : {})}
        disabled={disabled || full}
        onChange={(event) => {
          // Copied out of the FileList *before* the input is cleared.
          //
          // `event.target.files` is live: it is the input's own list, not a
          // snapshot, so emptying the input empties the thing this variable
          // points at. Clearing first left `take` with nothing to do — no
          // upload, no chip, no request — which is what "attaching a file does
          // nothing at all" turned out to be. jsdom does not reproduce the
          // live-clearing, so the tests were quite happy about it.
          //
          // The input still has to be cleared, or picking the same file twice
          // in a row fires no change the second time.
          const picked = [...(event.target.files ?? [])];
          event.target.value = "";
          take(picked);
        }}
      />

      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {value.map((file, index) => (
            <li key={`${file.name}-${index}`}>
              <FileChip
                file={file}
                onRemove={
                  disabled
                    ? undefined
                    : () => {
                        if (file.file) URL.revokeObjectURL(file.url);
                        onChange(value.filter((_, at) => at !== index));
                      }
                }
              />
            </li>
          ))}
        </ul>
      ) : null}

      {error ? (
        <p role="alert" className="text-danger text-footnote">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Several files, one after another, with the rest behind a count.
 *
 * `show` is how many fit where this is being drawn. The ticket panel is a
 * narrow column beside the conversation and a stack of thumbnails would push
 * everything it is there to say off the screen — so it shows one and says how
 * many more there are, and the gallery is where you look at them.
 */
export function AttachmentList({
  files,
  show,
  className,
}: {
  files: AttachmentDraft[];
  /** How many to draw. The rest are reachable through the count. */
  show?: number | undefined;
  className?: string;
}) {
  const t = useTranslation();
  const [gallery, setGallery] = useState<number | null>(null);

  if (files.length === 0) return null;
  const drawn = show === undefined ? files : files.slice(0, show);
  const hidden = files.length - drawn.length;

  return (
    <>
      <ul className={cn("flex flex-wrap items-center gap-2", className)}>
        {drawn.map((file, index) => (
          <li key={`${file.name}-${index}`}>
            <FileChip file={file} onOpen={() => setGallery(index)} />
          </li>
        ))}
        {hidden > 0 ? (
          <li>
            <button
              type="button"
              // Opens at the first one not drawn, which is the one the count is
              // about — starting at the beginning would reopen what is already
              // on screen.
              onClick={() => setGallery(drawn.length)}
              className={cn(chipClass, "font-medium")}
            >
              {t("hd.moreFiles").replace("{n}", String(hidden))}
            </button>
          </li>
        ) : null}
      </ul>
      {gallery !== null ? (
        <Gallery files={files} at={gallery} onClose={() => setGallery(null)} />
      ) : null}
    </>
  );
}

/**
 * Every file on the record, one at a time, with a way through them.
 *
 * Built on the app's one file viewer rather than beside it: the sheet, the
 * download and the way out are the same as everywhere else, and what this adds
 * is the pair of steps and the count in the footer.
 */
function Gallery({
  files,
  at,
  onClose,
}: {
  files: AttachmentDraft[];
  at: number;
  onClose: () => void;
}) {
  const t = useTranslation();
  const [index, setIndex] = useState(at);
  const file = files[index]!;
  // Wraps, because a gallery of three that stops at the third is a gallery you
  // have to close and reopen to see the first again.
  const step = (by: number) => setIndex((was) => (was + by + files.length) % files.length);

  return (
    <FilePreview
      src={file.url}
      title={file.name}
      download={file.name}
      onClose={onClose}
      actions={
        files.length > 1 ? (
          <span className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => step(-1)}>
              <ChevronLeft className="size-4" aria-hidden />
              {t("hd.previousFile")}
            </Button>
            <span className="text-fg-muted text-subhead tabular-nums">
              {t("hd.fileOfFiles")
                .replace("{n}", String(index + 1))
                .replace("{total}", String(files.length))}
            </span>
            <Button variant="secondary" onClick={() => step(1)}>
              {t("hd.nextFile")}
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </span>
        ) : undefined
      }
    />
  );
}

/** The Files tab: every file on the ticket and on its thread, in one place. */
export function FilesTab({ files }: { files: Attachment[] }) {
  const t = useTranslation();
  if (files.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title={t("hd.noFilesTitle")}
        description={t("hd.noFilesBody")}
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-fg-muted text-subhead">
        {t("hd.filesCount").replace("{n}", String(files.length))}
      </p>
      <AttachmentList files={files} />
    </div>
  );
}
